import { useQuery } from "@tanstack/react-query";
import { Alert, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { apiFetch, withQuery } from "../../api/client";
import type { DeviseStatusResponse } from "./PublicPage";
import { deviseError, PublicPage } from "./PublicPage";

type Flow = "confirmation" | "unlock";

const PARAMS: Record<Flow, string> = {
  confirmation: "confirmation_token",
  unlock: "unlock_token",
};

/**
 * /users/confirmation?confirmation_token=… and /users/unlock?unlock_token=… —
 * where the links in the confirmation and unlock mails land.
 *
 * Both endpoints do their work on a GET, so the screen has nothing to ask for:
 * it fires the request once and reports the answer. `retry: false` matters —
 * an expired or already spent token is a 422, and repeating it would only
 * produce the same refusal three more times.
 */
export function TokenLandingPage({ flow }: { flow: Flow }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get(PARAMS[flow]);

  const title =
    flow === "confirmation"
      ? t("auth.views.confirmations.resend_instructions")
      : t("auth.views.unlock.resend_instructions");

  const result = useQuery({
    queryKey: [flow, "token", token],
    queryFn: () =>
      apiFetch<DeviseStatusResponse>(
        withQuery(`/${flow}`, { [PARAMS[flow]]: token }),
        { anonymous: true },
      ),
    enabled: token !== null,
    retry: false,
  });

  return (
    <PublicPage title={title}>
      {token === null ? (
        <Alert type="warning" showIcon title={t("devise.passwords.no_token")} />
      ) : result.isPending ? (
        <Spin />
      ) : result.error ? (
        <Alert type="error" showIcon title={deviseError(result.error)} />
      ) : (
        <>
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            title={result.data?.status?.message}
          />
          <Link to="/login">{t("auth.sign_in")}</Link>
        </>
      )}
    </PublicPage>
  );
}
